import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import { IStoreLocatorDealer } from '@models/IStoreLocatorDealer';
import updateDataLayer from '@utils/helpers/gtm';
import { useEffect, useRef } from 'react';

interface Props {
  readonly dealer: IStoreLocatorDealer;
  readonly goToDealerLabel: string;
  readonly onCloseTooltip: () => void;
}

const DealersMapPinTooltip = ({
  dealer,
  goToDealerLabel,
  onCloseTooltip,
}: Props) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const tooltipContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        onCloseTooltip();

        tooltipContainerRef.current?.removeEventListener(
          'keydown',
          handleKeyDown
        );
      }
    };

    tooltipContainerRef.current?.addEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={tooltipContainerRef}
      className="relative py-2.5 pl-2.5 pr-1.5 lg:py-[18px] lg:pl-[18px] lg:pr-3 lg:mr-0.5"
    >
      <button
        ref={closeButtonRef}
        className="absolute top-0.5 right-0 cursor-pointer z-[1]"
        onClick={onCloseTooltip}
      >
        <Icon name="close" size="1.25rem" className="pointer-events-none" />
      </button>

      <p className="text-black-100 body-one body-one--bold mb-1 lg:mb-2.5">
        {dealer.title}
      </p>

      <div className="mb-3 lg:mb-5 flex items-center">
        <div className="flex mr-2 mb-[3px]">
          <Icon name="map_pin" size="10px" />
        </div>

        <div className="text-grey-600 body-four">
          {parseFloat(dealer.distance).toFixed(1)}
          {dealer.distanceMeasurement}
        </div>
      </div>

      {dealer.dealerUrl && (
        <div>
          <Button
            className="text-center"
            link={{
              name: goToDealerLabel,
              url: dealer.dealerUrl,
              target: '_blank',
            }}
            linkCallback={() => {
              updateDataLayer({
                event: 'storeFinder',
                content_type: 'store_listing_map',
                store_name: dealer.title,
                link_text: goToDealerLabel,
              });
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DealersMapPinTooltip;
